/*
 * Gourmet Easy warranty registration form.
 *
 * Handles the Online / Physical shop branching, client-side validation and
 * the submission to the app proxy route. The proxy does the real
 * work: customer find-or-create, receipt upload via fileCreate, computing
 * the expiry from product.custom.warranty_term, and writing the
 * warranty_registration metaobject.
 *
 * Until that route is deployed the endpoint setting is blank, and the form
 * says so plainly instead of appearing to succeed -- a warranty the owner
 * believes is registered but is not would be worse than an obvious error.
 */
if (!customElements.get('ge-warranty-form')) {
  customElements.define(
    'ge-warranty-form',
    class GeWarrantyForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.done = this.querySelector('[data-warranty-done]');
        this.alert = this.querySelector('[data-warranty-alert]');
        this.endpoint = (this.dataset.endpoint || '').trim();
        if (!this.form) return;

        this.branches = this.querySelectorAll('[data-branch]');
        this.sourceInputs = this.form.querySelectorAll('input[name="source"]');
        this.sourceInputs.forEach((input) =>
          input.addEventListener('change', () => this.showBranch(input.value))
        );

        const receipt = this.form.querySelector('input[name="receipt"]');
        if (receipt) {
          receipt.addEventListener('change', () => {
            const label = this.querySelector('[data-receipt-label]');
            const file = receipt.files && receipt.files[0];
            if (label) label.textContent = file ? file.name : 'Add a photo of your receipt';
          });
        }

        // A product whose term is "0 years" is explicitly not covered, as
        // distinct from one nobody has set a term for -- only the former
        // gets the notice.
        this.productSelect = this.form.querySelector('select[name="product_id"]');
        this.coverNote = this.querySelector('[data-cover-note]');
        this.coveredTemplate = this.dataset.coveredNote || '';
        this.uncoveredTemplate = this.dataset.uncoveredNote || '';
        if (this.productSelect && this.coverNote) {
          this.productSelect.addEventListener('change', () => this.showCoverNotice());
          this.showCoverNotice();
        }

        /* The submit button's resting text, captured before anything can
           overwrite it. It used to be read inside onSubmit and restored only
           on the failure path -- so a SUCCESSFUL registration left the label
           reading "Registering…" under a hidden form, and "Register another
           tool" brought that back. reset() re-enabled the button but had no
           way to know what it should say. One source, set once, used by both
           the failure path and reset. */
        this.submitButton = this.form.querySelector('button[type="submit"]');
        this.submitLabel = this.querySelector('[data-submit-label]');
        this.submitLabelText = this.submitLabel ? this.submitLabel.textContent : '';

        this.setUpRetailerFilter();

        this.form.addEventListener('submit', (event) => this.onSubmit(event));

        const reset = this.querySelector('[data-warranty-reset]');
        if (reset) reset.addEventListener('click', () => this.reset());

        // Disable the branches now rather than on the first change event:
        // left enabled, a submit with no source picked flagged four fields
        // nobody can see and focused one of them.
        //
        // Read the checked radio rather than passing null, because browsers
        // restore radio state on back-navigation and soft reload. Passing
        // null there collapsed the branch and wiped its values while the
        // radio still showed as chosen.
        const restored = this.form.querySelector('input[name="source"]:checked');
        this.showBranch(restored ? restored.value : null);
      }

      /**
       * Narrows "Which retailer" to the chosen country.
       *
       * Each option carries data-countries, ";"-separated; an empty one means
       * the retailer shows everywhere (Amazon, Walmart, the catch-all). If no
       * retailer covers the chosen country the whole list is shown instead of
       * an empty one -- a customer in a country we have no marketplace row for
       * still bought the tool somewhere.
       *
       * Options are removed and re-added rather than hidden: `hidden` on an
       * <option> is honoured inconsistently across browsers, and a "hidden"
       * option that is still selectable by keyboard is worse than no filter.
       */
      setUpRetailerFilter() {
        this.countrySelect = this.form.querySelector('select[name="country"]');
        this.retailerSelect = this.form.querySelector('[data-branch="online"] select[name="retailer"]');
        if (!this.countrySelect || !this.retailerSelect) return;

        this.retailerOptions = Array.from(this.retailerSelect.options);
        this.countrySelect.addEventListener('change', () => this.filterRetailers());
        this.filterRetailers();
      }

      filterRetailers() {
        const country = this.countrySelect.value;
        const countriesFor = (option) =>
          (option.dataset.countries || '')
            .split(';')
            .map((name) => name.trim())
            .filter(Boolean);

        const covered = this.retailerOptions.filter((option) =>
          countriesFor(option).includes(country)
        );

        const keep = (option) => {
          if (!option.value) return true; // the "Select retailer" placeholder
          if (!country || !covered.length) return true;
          return countriesFor(option).length === 0 || countriesFor(option).includes(country);
        };

        const chosen = this.retailerSelect.value;
        const visible = this.retailerOptions.filter(keep);
        this.retailerSelect.replaceChildren(...visible);
        // Keep the answer if it survived the filter; otherwise make them pick
        // again rather than silently submitting a retailer they cannot see.
        this.retailerSelect.value = visible.some((option) => option.value === chosen) ? chosen : '';
      }

      /* Only one branch is ever active, and the inactive one is disabled as
         well as hidden -- a hidden-but-enabled field still submits, and the
         legacy data is full of order IDs sitting on receipt registrations. */
      showBranch(value) {
        this.branches.forEach((branch) => {
          const active = branch.dataset.branch === value;
          branch.hidden = !active;
          branch.querySelectorAll('input, select').forEach((field) => {
            field.disabled = !active;
            if (!active) field.value = '';
          });
        });
        const label = this.querySelector('[data-receipt-label]');
        if (label && value !== 'shop') label.textContent = 'Add a photo of your receipt';
        this.clearError(this.querySelector('[data-source-error]'));
      }

      /**
       * Says what cover the chosen tool carries. Three outcomes, and the
       * blank one matters: a product nobody has set a term for says
       * nothing, rather than claiming either cover or none.
       */
      showCoverNotice() {
        const option = this.productSelect.selectedOptions[0];
        const term = (option && option.dataset.term ? option.dataset.term : '').trim();
        const years = parseInt(term, 10);
        const note = this.coverNote;

        if (term === '' || !Number.isFinite(years)) {
          note.hidden = true;
          return;
        }

        if (years === 0) {
          note.textContent = this.uncoveredTemplate;
          note.classList.remove('ge-warranty-cover-note--covered');
          note.hidden = false;
          return;
        }

        // 100 years is the stored encoding for lifetime; customers see the
        // word, never the number.
        const label = years >= 100 ? 'a lifetime' : years + (years === 1 ? ' year' : ' years');
        note.textContent = this.coveredTemplate.replace('[term]', label);
        note.classList.add('ge-warranty-cover-note--covered');
        note.hidden = false;
      }

      setError(field, message) {
        // The receipt input sits in its own label rather than a .ge-field, so
        // falling back to the branch wrapper found that branch's *first*
        // error slot -- the receipt's message printed under "Which shop".
        // data-error-for names the slot explicitly where the shape differs.
        const named = field.dataset.errorFor
          ? this.querySelector(`[data-field-error="${field.dataset.errorFor}"]`)
          : null;
        const holder = field.closest('.ge-field, [data-branch]');
        const slot = named || (holder && holder.querySelector('[data-field-error]'));
        field.classList.add('ge-input--invalid');
        field.setAttribute('aria-invalid', 'true');
        if (slot) {
          slot.textContent = message;
          slot.hidden = false;
        }
      }

      clearError(slot) {
        if (slot) slot.hidden = true;
      }

      clearAllErrors() {
        this.form.querySelectorAll('[data-field-error]').forEach((slot) => (slot.hidden = true));
        this.clearError(this.querySelector('[data-source-error]'));
        this.form.querySelectorAll('.ge-input--invalid').forEach((field) => {
          field.classList.remove('ge-input--invalid');
          field.removeAttribute('aria-invalid');
        });
        if (this.alert) this.alert.hidden = true;
      }

      validate() {
        this.clearAllErrors();
        let firstInvalid = null;

        const required = this.form.querySelectorAll('[data-error]');
        required.forEach((field) => {
          if (field.disabled) return;
          const isFile = field.type === 'file';
          const empty = isFile ? !(field.files && field.files.length) : !field.value.trim();
          const badEmail =
            field.type === 'email' && field.value.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(field.value);
          if (empty || badEmail) {
            this.setError(field, field.dataset.error);
            if (!firstInvalid) firstInvalid = field;
          }
        });

        const source = this.form.querySelector('input[name="source"]:checked');
        if (!source) {
          const slot = this.querySelector('[data-source-error]');
          if (slot) slot.hidden = false;
          if (!firstInvalid) firstInvalid = this.sourceInputs[0];
        }

        if (firstInvalid) firstInvalid.focus();
        return !firstInvalid;
      }

      showAlert(message) {
        if (!this.alert) return;
        this.alert.textContent = message;
        this.alert.hidden = false;
        this.alert.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }

      async onSubmit(event) {
        event.preventDefault();
        if (!this.validate()) return;

        if (!this.endpoint) {
          this.showAlert(
            'Online registration is not switched on yet. Email love@gourmeteasyonline.com with your ' +
              'product and order number and we will register it for you.'
          );
          return;
        }

        this.setSubmitBusy(true);

        try {
          const response = await fetch(this.endpoint, {
            method: 'POST',
            body: new FormData(this.form),
          });
          const payload = await response.json().catch(() => ({}));

          if (!response.ok || payload.ok === false) {
            // Flagged so the catch below can tell a message we wrote from a
            // browser one. `TypeError: Failed to fetch` also has a truthy
            // .message, which used to reach the customer verbatim and made
            // the friendly fallback -- the one carrying the support address
            // -- unreachable on exactly the failure it was written for.
            throw Object.assign(new Error(payload.message || 'That did not go through.'), {
              fromServer: true,
            });
          }
          this.succeed(this.form.querySelector('input[name="owner_email"]').value.trim(), payload);
        } catch (error) {
          this.showAlert(
            (error && error.fromServer && error.message) ||
              'That did not go through. Check your connection and try again, or email ' +
                'love@gourmeteasyonline.com.'
          );
          this.setSubmitBusy(false);
        }
      }

      /* Busy and resting states in one place, so they cannot drift apart the
         way they did when only the error path knew how to undo the busy one. */
      setSubmitBusy(busy) {
        if (this.submitButton) this.submitButton.disabled = busy;
        if (this.submitLabel) {
          this.submitLabel.textContent = busy ? 'Registering…' : this.submitLabelText;
        }
      }

      succeed(email, payload) {
        const heading = this.querySelector('[data-done-heading]');
        const body = this.querySelector('[data-done-body]');
        const target = this.querySelector('[data-done-email]');
        if (heading) heading.textContent = this.dataset.successHeading;
        if (body) body.textContent = this.dataset.successBody;
        if (target) target.textContent = email;

        // Read before the form is hidden: reset() clears it later, and the
        // review and kit panels need the tool and the owner's name.
        const option = this.productSelect && this.productSelect.selectedOptions[0];
        const nameInput = this.form.querySelector('input[name="owner_name"]');
        this.registered = option && option.value
          ? {
              id: option.value,
              name: option.dataset.name || '',
              url: option.dataset.url || '',
              image: option.dataset.image || '',
              type: option.dataset.type || '',
              term: parseInt(option.dataset.term || '', 10),
              asin: (option.dataset.asin || '').trim(),
            }
          : null;
        this.ownerName = nameInput ? nameInput.value.trim() : '';
        // Where it was bought, for sending the review back there. Only the
        // active branch's retailer field is enabled (see showBranch).
        const retailerField = this.form.querySelector('[name="retailer"]:not(:disabled)');
        this.purchase = {
          retailer: retailerField ? retailerField.value.trim() : '',
          country: this.countrySelect ? this.countrySelect.value : '',
          inStore: Boolean(this.form.querySelector('input[name="source"][value="shop"]:checked')),
        };

        this.form.hidden = true;
        // The confirmation takes the full width: the cover and help panels
        // beside the form have nothing left to say once it is done.
        const page = this.closest('.ge-warranty');
        if (page) page.classList.add('ge-warranty--done');
        if (this.done) {
          this.done.hidden = false;
          this.done.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }

        if (this.registered) {
          this.fillProduct(this.registered);
          this.showReview(payload && payload.review);
          this.showKit(this.registered);
        }
      }

      /* ---- The tool just registered, everywhere the confirmation names it ---- */

      fillProduct(product) {
        this.querySelectorAll('[data-product-name]').forEach((el) => (el.textContent = product.name));
        this.querySelectorAll('[data-product-link]').forEach((el) => (el.href = product.url));
        [this.querySelector('[data-review-image]'), this.querySelector('[data-chip-image]')].forEach((img) => {
          if (!img || !product.image) return;
          img.src = product.image;
          img.hidden = false;
          const holder = img.closest('[data-review-thumb]');
          if (holder) holder.hidden = false;
        });
        const term = this.querySelector('[data-chip-term]');
        if (term) {
          const years = product.term;
          const cover = !Number.isFinite(years) || years <= 0
            ? ''
            : years >= 100 ? 'Lifetime warranty · ' : years + '-year warranty · ';
          term.textContent = cover + 'registered today';
        }
      }

      /* ---- Review ---- */

      /**
       * Only for a new registration: the register response carries a signed,
       * short-lived token for it, and the worker posts the review under the
       * name, email and product on that record. No token, no box.
       */
      showReview(review) {
        const panel = this.querySelector('[data-review]');
        const endpoint = (this.dataset.reviewEndpoint || '').trim();
        if (!panel || !endpoint || !review || !review.token) return;

        this.review = review;
        const form = panel.querySelector('[data-review-form]');

        // Bought somewhere that takes reviews: the form still comes first --
        // reviews collected here are the ones Google Shopping can use -- with
        // a quieter line under it offering to review it there instead.
        const away = this.reviewElsewhere();
        panel.querySelector('[data-review-away]').hidden = !away;
        if (away) this.showReviewAway(panel, away);

        const parts = this.ownerName.split(/\s+/).filter(Boolean);
        const first = parts[0] || '';
        const shown = parts.length > 1 ? first + ' ' + parts[parts.length - 1].charAt(0).toUpperCase() + '.' : first;
        panel.querySelector('[data-review-name]').textContent = shown;
        panel.querySelector('[data-review-first]').textContent = first || 'you';

        if (!this.reviewWired) {
          this.reviewWired = true;
          const words = ['Tap a star', 'Poor', 'Fair', 'Good', 'Very good', 'Excellent'];
          form.addEventListener('change', (event) => {
            if (event.target.name !== 'rating') return;
            const rating = parseInt(event.target.value, 10);
            panel.querySelectorAll('[data-star]').forEach((star) =>
              star.classList.toggle('is-on', parseInt(star.dataset.star, 10) <= rating)
            );
            panel.querySelector('[data-rating-word]').textContent = words[rating] || words[0];
            this.reviewError('');
          });
          form.addEventListener('submit', (event) => this.submitReview(event, endpoint));
          panel.querySelector('[data-review-skip]').addEventListener('click', () => this.reviewDone('skipped'));
        }
        panel.hidden = false;
      }

      /**
       * The "Retailers that take reviews" entry for where this tool was
       * bought, with its links filled in -- or null, meaning the review form
       * on this page. Null for this store (it is not in the list), for a
       * retailer that takes no reviews or that we cannot link to (likewise),
       * and for an entry whose links cannot be filled in.
       *
       * The retailer matches when its squashed name contains the entry's,
       * so "Castorama-FR" matches "Castorama" and a typed "Bunnings
       * Warehouse" matches "Bunnings".
       */
      reviewElsewhere() {
        const source = this.querySelector('script[data-review-elsewhere]');
        const retailer = this.purchase && this.purchase.retailer;
        if (!source || !retailer) return null;
        let entries;
        try {
          entries = JSON.parse(source.textContent);
        } catch (error) {
          return null;
        }
        const squash = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const bought = squash(retailer);
        const entry = entries.find((candidate) => squash(candidate.name) && bought.includes(squash(candidate.name)));
        if (!entry) return null;

        // The Amazon site a buyer in each country orders from. Unlisted EU
        // countries default to amazon.de, everywhere else to amazon.com. Every
        // country on the form that is not a direct market counts as EU here,
        // so a new direct market needs adding to the list below as well.
        // Ireland has had its own site since 2025.
        const amazonSites = {
          'United States': 'amazon.com',
          Canada: 'amazon.ca',
          'United Kingdom': 'amazon.co.uk',
          Ireland: 'amazon.ie',
          Australia: 'amazon.com.au',
          Germany: 'amazon.de',
          Austria: 'amazon.de',
          France: 'amazon.fr',
          Luxembourg: 'amazon.fr',
          Italy: 'amazon.it',
          Spain: 'amazon.es',
          Portugal: 'amazon.es',
          Netherlands: 'amazon.nl',
          Belgium: 'amazon.com.be',
          Sweden: 'amazon.se',
          Poland: 'amazon.pl',
          'United Arab Emirates': 'amazon.ae',
        };
        const euCountry = this.countrySelect &&
          Array.from(this.countrySelect.options).some((option) => option.value === this.purchase.country) &&
          !['United States', 'Canada', 'United Kingdom', 'Australia', 'United Arab Emirates', 'Other Countries', ''].includes(this.purchase.country);
        const amazon = amazonSites[this.purchase.country] || (euCountry ? 'amazon.de' : 'amazon.com');
        const asin = (this.registered && this.registered.asin) || '';

        // A link naming {asin} is useless without one, so it comes back
        // empty and the backup link is used instead.
        const fill = (link) => {
          if (!link) return '';
          if (link.includes('{asin}') && !/^[A-Z0-9]{10}$/.test(asin)) return '';
          return link.replace(/\{asin\}/g, asin).replace(/\{amazon\}/g, amazon);
        };
        const link = fill(entry.link);
        const backup = fill(entry.fallback);
        if (!link && !backup) return null;
        return {
          // "Amazon.co.uk", not "Amazon": it says which site the link opens.
          name: /\{amazon\}/.test(entry.link) ? 'A' + amazon.slice(1) : entry.name,
          link: link || backup,
          // Straight to this product's review page, rather than to the site.
          direct: Boolean(link) && /\{asin\}/.test(entry.link),
          backup: link && backup && backup !== link ? backup : '',
        };
      }

      showReviewAway(panel, away) {
        let text = 'Bought it on ' + away.name + '? You can review it there instead:';
        // Not straight to the product's review page, so say where to look.
        if (!away.direct) {
          text = this.purchase.inStore
            ? 'Bought it at ' + away.name + '? You can review it on their site instead:'
            : 'Bought it from ' + away.name + '? You can review it there instead, from your orders:';
        }
        panel.querySelector('[data-review-away-text]').textContent = text;
        const link = panel.querySelector('[data-review-away-link]');
        link.href = away.link;
        link.textContent = (away.direct ? 'review it on ' : 'go to ') + away.name;
        const alt = panel.querySelector('[data-review-away-alt]');
        alt.hidden = !away.backup;
        if (away.backup) panel.querySelector('[data-review-away-fallback]').href = away.backup;
      }

      reviewError(message) {
        const slot = this.querySelector('[data-review-error]');
        if (!slot) return;
        slot.textContent = message;
        slot.hidden = !message;
      }

      async submitReview(event, endpoint) {
        event.preventDefault();
        const form = event.currentTarget;
        const rating = form.querySelector('input[name="rating"]:checked');
        const body = form.querySelector('textarea[name="body"]');
        if (!rating) return this.reviewError('Pick a star rating first.');
        if (!body.value.trim()) {
          body.focus();
          return this.reviewError('Add a line about it first.');
        }

        const button = form.querySelector('[data-review-submit]');
        button.disabled = true;
        const label = button.textContent;
        button.textContent = 'Posting…';
        this.reviewError('');

        const data = new FormData();
        data.append('registration', this.review.registration);
        data.append('issued', this.review.issued);
        data.append('token', this.review.token);
        data.append('rating', rating.value);
        data.append('body', body.value.trim());

        try {
          const response = await fetch(endpoint, { method: 'POST', body: data });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok || payload.ok === false) {
            throw Object.assign(new Error(payload.message || ''), { fromServer: true });
          }
          this.reviewDone('thanks');
        } catch (error) {
          this.reviewError(
            (error && error.fromServer && error.message) ||
              'That did not go through. Check your connection and try again.'
          );
          button.disabled = false;
          button.textContent = label;
        }
      }

      reviewDone(which) {
        const panel = this.querySelector('[data-review]');
        panel.querySelector('[data-review-ask]').hidden = true;
        const after = panel.querySelector(which === 'thanks' ? '[data-review-thanks]' : '[data-review-skipped]');
        after.hidden = false;
        after.focus({ preventScroll: true });
      }

      /* ---- Round out the kit ---- */

      showKit(product) {
        const kit = this.querySelector('[data-kit]');
        if (!kit) return;
        const offer = kit.querySelector('[data-kit-offer]');
        const recipe = this.showRecipe(product.id);
        kit.hidden = !(offer || recipe);
        if (offer && !this.offerWired) {
          this.offerWired = true;
          offer.querySelector('[data-offer-apply]').addEventListener('click', (event) => this.applyOffer(event.currentTarget));
        }
        this.loadCards(product).then((shown) => {
          if (shown) kit.hidden = false;
        });
      }

      showRecipe(productId) {
        const link = this.querySelector('[data-kit-recipe]');
        const source = this.querySelector('[data-recipe-map]');
        if (!link || !source) return false;
        let recipe = null;
        try {
          recipe = JSON.parse(source.textContent)[productId] || null;
        } catch (error) {
          recipe = null;
        }
        link.hidden = !recipe;
        if (!recipe) return false;

        const tools = (recipe.tools || []).filter(Boolean);
        const madeWith = tools.length > 1
          ? tools.slice(0, -1).join(', ') + ' and ' + tools[tools.length - 1]
          : tools[0] || '';
        link.href = recipe.url;
        link.querySelector('[data-recipe-image]').src = recipe.image;
        link.querySelector('[data-recipe-name]').textContent = recipe.name;
        link.querySelector('[data-recipe-meta]').textContent =
          (recipe.by ? 'By ' + recipe.by + '. ' : '') + (madeWith ? 'Made with ' + madeWith + '.' : '');
        return true;
      }

      /**
       * Shopify's related products for the tool, rendered by
       * sections/ge-warranty-recs.liquid so prices are in the visitor's
       * currency. Anything sharing the tool's type word is skipped, then the
       * first three are shown. Resolves to whether any card is showing.
       */
      async loadCards(product) {
        const holder = this.querySelector('[data-kit-list]');
        const block = this.querySelector('[data-kit-products]');
        if (!holder || !block) return false;
        holder.replaceChildren();
        block.hidden = true;

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const url = root + 'recommendations/products?section_id=ge-warranty-recs&intent=related&limit=10&product_id=' +
          encodeURIComponent(product.id);
        try {
          const response = await fetch(url);
          if (!response.ok) return false;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const grid = doc.querySelector('.ge-wkit__grid');
          if (!grid) return false;
          const cards = Array.from(grid.querySelectorAll('[data-product]'));
          // One of each kind as well: Shopify's list for Garlic Easy holds
          // three peelers, and three peelers is not a kit.
          const seen = new Set(product.type ? [product.type] : []);
          const keep = cards
            .filter((card) => card.dataset.product !== String(product.id))
            .filter((card) => {
              const type = card.dataset.type || card.dataset.product;
              if (seen.has(type)) return false;
              seen.add(type);
              return true;
            })
            .slice(0, 3);
          if (!keep.length) return false;
          cards.forEach((card) => {
            if (keep.includes(card)) card.hidden = false;
            else card.remove();
          });
          // importNode so the <ge-bundle-add> buttons upgrade in this document.
          holder.appendChild(document.importNode(grid, true));
          block.hidden = false;
          const keepShopping = this.querySelector('[data-keep-shopping]');
          if (keepShopping) keepShopping.hidden = true;
          return true;
        } catch (error) {
          return false;
        }
      }

      /**
       * Shopify's shareable discount link, fetched rather than followed so the
       * customer stays on this page. It stores the code against their session
       * and it comes off at checkout. The code stays printed either way.
       */
      async applyOffer(button) {
        const code = button.dataset.code;
        const note = this.querySelector('[data-offer-note]');
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        button.disabled = true;
        try {
          const response = await fetch(
            root + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(root + 'cart.js')
          );
          if (!response.ok) throw new Error('discount link failed');
          button.textContent = 'Applied';
          if (note) note.textContent = 'Saved to your cart. It comes off at checkout.';
        } catch (error) {
          button.disabled = false;
          if (note) note.textContent = 'Enter ' + code + ' at checkout.';
        }
      }

      /* Back to a clean confirmation for the next registration. */
      resetExtras() {
        const panel = this.querySelector('[data-review]');
        if (panel) {
          panel.hidden = true;
          const form = panel.querySelector('[data-review-form]');
          form.reset();
          panel.querySelector('[data-review-away]').hidden = true;
          const submit = form.querySelector('[data-review-submit]');
          submit.disabled = false;
          submit.textContent = 'Post my review';
          panel.querySelectorAll('[data-star]').forEach((star) => star.classList.remove('is-on'));
          panel.querySelector('[data-rating-word]').textContent = 'Tap a star';
          panel.querySelector('[data-review-ask]').hidden = false;
          panel.querySelector('[data-review-thanks]').hidden = true;
          panel.querySelector('[data-review-skipped]').hidden = true;
          this.reviewError('');
        }
        this.review = null;
        const kit = this.querySelector('[data-kit]');
        if (kit) kit.hidden = true;
        const recipe = this.querySelector('[data-kit-recipe]');
        if (recipe) recipe.hidden = true;
        const keepShopping = this.querySelector('[data-keep-shopping]');
        if (keepShopping) keepShopping.hidden = false;
      }

      reset() {
        this.form.reset();
        this.clearAllErrors();
        this.showBranch(null);
        // form.reset() puts the product select back to "Select your product"
        // but leaves the notice describing the tool just registered.
        if (this.productSelect && this.coverNote) this.showCoverNotice();
        this.setSubmitBusy(false);
        if (this.done) this.done.hidden = true;
        const page = this.closest('.ge-warranty');
        if (page) page.classList.remove('ge-warranty--done');
        this.resetExtras();
        this.form.hidden = false;
        this.form.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  );
}
